// Quote Builder — customer name typeahead.
//
// Replaces source's flat `<datalist>` (kos-qb-v3.js:1455) with a richer
// dropdown showing name · project · lead-score. The endpoint
// (/api/crm/contacts) serves the retailer's own contacts; phone + email
// arrive masked per `contact_mask.js` — that's intentional and exactly
// the "masked display" Steve approved. The unmasked values are resolved
// server-side at send time via `customerId` (see customer_portal.js
// /api/quotes/send).

import type { QuoteStore } from "./state";
import type { PageContext } from "../../shell/types";
import { escapeHtml } from "./formatters";

/** Shape returned by GET /api/crm/contacts (masked email/phone). */
export interface CrmContact {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  stage?: string;
  project?: string;
  /** Lead score 0–100 (modules/crm.js pgRowToContact). */
  score?: number;
  /** Free-form project / kitchen type from the CRM record. */
  kitchenType?: string;
}

interface ContactsResponse {
  ok?: boolean;
  contacts?: CrmContact[];
}

const MAX_VISIBLE = 8;
const DEBOUNCE_MS = 300;

// Per-page cache so we only hit /api/crm/contacts once per mount; the
// dropdown filters this in-memory list as the user types.
let cached: CrmContact[] | null = null;
let inflight: Promise<CrmContact[]> | null = null;

async function fetchContacts(ctx: PageContext): Promise<CrmContact[]> {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = ctx.api<ContactsResponse>("/crm/contacts")
    .then((d) => {
      cached = Array.isArray(d.contacts) ? d.contacts : [];
      return cached;
    })
    .catch((err) => {
      console.warn("[qb customer-typeahead] /crm/contacts failed", err);
      cached = [];
      return cached;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

/** Tint per CRM stage — matches the rgba palette Steve approved (verbatim hex
 *  bands from portal-standalone.html:6964 stage table). */
function stageTint(stage: string): { fg: string; bg: string } {
  const s = String(stage || "").toLowerCase();
  if (s.startsWith("won"))       return { fg: "#22c55e", bg: "rgba(34, 197, 94, 0.12)" };
  if (s.startsWith("quoted"))    return { fg: "#e58200", bg: "rgba(229, 130, 0, 0.12)" };
  if (s.startsWith("qualified")) return { fg: "#a855f7", bg: "rgba(168, 85, 247, 0.12)" };
  if (s.startsWith("lost"))      return { fg: "#ef4444", bg: "rgba(239, 68, 68, 0.12)" };
  return { fg: "#3b82f6", bg: "rgba(59, 130, 246, 0.12)" }; // Lead default
}

function rankByQuery(contacts: CrmContact[], q: string): CrmContact[] {
  const ql = q.toLowerCase().trim();
  if (!ql) return contacts.slice(0, MAX_VISIBLE);
  const startsWith: CrmContact[] = [];
  const contains: CrmContact[] = [];
  for (const c of contacts) {
    const nl = String(c.name || "").toLowerCase();
    if (!nl) continue;
    if (nl.startsWith(ql)) startsWith.push(c);
    else if (nl.includes(ql)) contains.push(c);
  }
  return startsWith.concat(contains).slice(0, MAX_VISIBLE);
}

function renderDropdown(matches: CrmContact[]): string {
  if (matches.length === 0) {
    return `<div class="qb-cust-dd__empty">No matches — keep typing for free-text entry</div>`;
  }
  return matches.map((c, i) => {
    const project = c.project || c.kitchenType || "";
    const score = typeof c.score === "number" ? c.score : null;
    const tint = stageTint(c.stage || "");
    return `
      <button type="button" role="option" class="qb-cust-dd__row" data-contact-id="${escapeHtml(c.id)}" data-row-idx="${i}">
        <span class="qb-cust-dd__name">${escapeHtml(c.name)}</span>
        ${project ? `<span class="qb-cust-dd__project">${escapeHtml(project)}</span>` : ""}
        ${c.stage ? `<span class="qb-cust-dd__stage" style="color:${tint.fg};background:${tint.bg}">${escapeHtml(c.stage)}</span>` : ""}
        ${score !== null ? `<span class="qb-cust-dd__score">${score}</span>` : ""}
      </button>
    `;
  }).join("");
}

export interface CustomerTypeaheadHandle {
  open(): void;
  close(): void;
  destroy(): void;
}

export function mountCustomerTypeahead(
  inputEl: HTMLInputElement,
  store: QuoteStore,
  ctx: PageContext,
): CustomerTypeaheadHandle {
  // Build wrapper + dropdown adjacent to the input so it can position absolutely.
  const wrap = document.createElement("div");
  wrap.className = "qb-cust-dd-wrap";
  inputEl.parentNode?.insertBefore(wrap, inputEl);
  wrap.appendChild(inputEl);

  const dd = document.createElement("div");
  dd.className = "qb-cust-dd";
  dd.setAttribute("role", "listbox");
  dd.hidden = true;
  wrap.appendChild(dd);

  let activeIdx = -1;
  let lastMatches: CrmContact[] = [];

  function highlight(): void {
    const rows = dd.querySelectorAll<HTMLButtonElement>(".qb-cust-dd__row");
    rows.forEach((r, i) => r.classList.toggle("is-active", i === activeIdx));
  }

  async function refresh(): Promise<void> {
    const all = await fetchContacts(ctx);
    lastMatches = rankByQuery(all, inputEl.value);
    dd.innerHTML = renderDropdown(lastMatches);
    activeIdx = -1;
    dd.hidden = false;
    adjustDropdownWidth();
  }

  function close(): void {
    dd.hidden = true;
    activeIdx = -1;
  }

  // Make the dropdown at least as wide as the surrounding `.qb-meta` panel
  // (Customer cell is only one column of an auto-fit grid, so the input
  // itself is far too narrow for name + project + stage + score). Shifts
  // left if the right edge would overflow the viewport.
  function adjustDropdownWidth(): void {
    const meta = wrap.closest<HTMLElement>(".qb-meta");
    if (!meta) return;
    const metaRect = meta.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    const styles = window.getComputedStyle(meta);
    const padLeft = parseFloat(styles.paddingLeft) || 0;
    const padRight = parseFloat(styles.paddingRight) || 0;
    const innerWidth = metaRect.width - padLeft - padRight;
    let offsetLeft = metaRect.left + padLeft - wrapRect.left;
    // Clamp so the dropdown can't overflow the right viewport edge.
    const viewportRight = window.innerWidth - 8;
    const ddRight = wrapRect.left + offsetLeft + innerWidth;
    if (ddRight > viewportRight) offsetLeft -= (ddRight - viewportRight);
    dd.style.left = `${offsetLeft}px`;
    dd.style.right = "auto";
    dd.style.width = `${Math.max(innerWidth, 320)}px`;
  }

  function select(contact: CrmContact): void {
    const cleanName = String(contact.name || "").trim();
    inputEl.value = cleanName;
    store.set((d) => {
      d.customer = cleanName;
      d.customerId = contact.id;
      d.customerEmail = contact.email ?? "";
      d.customerPhone = contact.phone ?? "";
    });
    close();
    // Blur so the meta-strip re-renders (store.subscribe skips re-render while
    // input is focused) and the "CRM linked" badge appears immediately.
    inputEl.blur();
  }

  let debounceTimer: number | null = null;
  function onInput(): void {
    // Typing clears any previous CRM link — until they pick or commit
    // the same name, the quote is "free text" again.
    store.set((d) => {
      d.customer = inputEl.value;
      d.customerId = "";
      d.customerEmail = "";
      d.customerPhone = "";
    });
    if (debounceTimer !== null) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => { void refresh(); }, DEBOUNCE_MS);
  }

  function onFocus(): void { void refresh(); }

  function onKeyDown(e: KeyboardEvent): void {
    if (e.key === "Enter") {
      // Always swallow Enter on this input — an ancestor <form> would
      // otherwise submit and refresh the page. If the dropdown is open
      // with a highlighted match (or only one match), select it.
      e.preventDefault();
      if (!dd.hidden) {
        const idx = activeIdx >= 0 ? activeIdx : (lastMatches.length === 1 ? 0 : -1);
        if (idx >= 0 && lastMatches[idx]) {
          select(lastMatches[idx]!);
        } else {
          close();
        }
      }
      return;
    }
    if (dd.hidden) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      activeIdx = Math.min(activeIdx + 1, lastMatches.length - 1);
      highlight();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      activeIdx = Math.max(activeIdx - 1, -1);
      highlight();
    } else if (e.key === "Escape") {
      close();
    }
  }

  function onDocMouseDown(e: MouseEvent): void {
    if (!wrap.contains(e.target as Node)) close();
  }

  function onDdMouseDown(e: MouseEvent): void {
    const row = (e.target as HTMLElement).closest<HTMLButtonElement>(".qb-cust-dd__row");
    if (!row) return;
    e.preventDefault(); // prevent input blur before click fires
    const id = row.dataset["contactId"];
    const match = lastMatches.find((c) => c.id === id);
    if (match) select(match);
  }

  function onResize(): void {
    if (!dd.hidden) adjustDropdownWidth();
  }

  inputEl.addEventListener("input", onInput);
  inputEl.addEventListener("focus", onFocus);
  inputEl.addEventListener("keydown", onKeyDown);
  dd.addEventListener("mousedown", onDdMouseDown);
  document.addEventListener("mousedown", onDocMouseDown);
  window.addEventListener("resize", onResize);

  return {
    open: () => { void refresh(); },
    close,
    destroy: () => {
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      inputEl.removeEventListener("input", onInput);
      inputEl.removeEventListener("focus", onFocus);
      inputEl.removeEventListener("keydown", onKeyDown);
      dd.removeEventListener("mousedown", onDdMouseDown);
      document.removeEventListener("mousedown", onDocMouseDown);
      window.removeEventListener("resize", onResize);
    },
  };
}

/** Reset the per-page cache. Call when the retailer might have updated their
 *  CRM (e.g. after creating a contact via the auto-save flow). */
export function invalidateCustomerCache(): void {
  cached = null;
}
